/**
 * TripPerFamilyCard — iter189 Phase 2.
 * Trip-only Overview card: lists each member's "party" (adults + kids)
 * with their net coin balance. Tappable → opens that member's ledger.
 *
 * Data shape (per iter183):
 *   group.party_composition: { [user_id]: { adults, kids, nights? } }
 *   group.kid_head_weight:   number (default 1.0)
 *   group.coin_balances:     { [user_id]: number }
 *   group.members:           [{ id, name, ... }]
 *
 * Hidden when (a) typeConfig.isTrip is false, (b) group has no party_composition.
 */
import { Users, ChevronRight } from 'lucide-react';
import { Card } from './ui/card';
import { formatNXC } from '../lib/formatNXC';

export default function TripPerFamilyCard({ ctx, typeConfig }) {
  const group = ctx?.group;
  if (!typeConfig?.isTrip) return null;
  if (!group) return null;

  const composition = group.party_composition || {};
  const compositionKeys = Object.keys(composition);
  if (compositionKeys.length === 0) return null;

  const members = group.members || [];
  const memberById = Object.fromEntries(members.map((m) => [m.id, m]));
  const coinBalances = group.coin_balances || {};
  const kidWeight = typeof group.kid_head_weight === 'number' ? group.kid_head_weight : 1;
  const currency = group.currency || 'INR';

  const rows = compositionKeys
    .map((uid) => {
      const p = composition[uid] || {};
      const adults = Math.max(0, Math.round(Number(p.adults || 0)));
      const kids = Math.max(0, Math.round(Number(p.kids || 0)));
      const nights = p.nights ? Math.round(Number(p.nights)) : null;
      const heads = adults + kids * kidWeight;
      const member = memberById[uid];
      if (!member) return null;
      const balance = Math.round(coinBalances[uid] || 0);
      return { uid, name: member.name, adults, kids, nights, heads, balance };
    })
    .filter(Boolean)
    .sort((a, b) => a.balance - b.balance); // most-negative first

  if (rows.length === 0) return null;

  return (
    <Card className="p-4 rounded-2xl border-2 border-border bg-card" data-testid="trip-per-family-card">
      <div className="flex items-center gap-2 mb-3">
        <Users className="w-4 h-4 text-primary" />
        <h3 className="font-bold text-sm tracking-tight">Per family / party</h3>
      </div>
      <div className="space-y-1.5">
        {rows.map((r) => (
          <button
            key={r.uid}
            type="button"
            onClick={() => ctx?.openMemberLedger?.({ id: r.uid, name: r.name })}
            disabled={!ctx?.openMemberLedger}
            data-testid={`trip-party-row-${r.uid}`}
            className="w-full flex items-center justify-between py-2 px-2 -mx-2 rounded-xl hover:bg-accent active:scale-[0.99] transition disabled:opacity-60 disabled:hover:bg-transparent text-left"
          >
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold truncate">{r.name}</p>
              <p className="text-xs text-muted-foreground tabular-nums">
                {r.adults} adult{r.adults === 1 ? '' : 's'}
                {r.kids > 0 && ` · ${r.kids} kid${r.kids === 1 ? '' : 's'}`}
                {' · '}{r.heads.toFixed(1)} hd
                {r.nights ? ` · ${r.nights}n` : ''}
              </p>
            </div>
            <div className="flex items-center gap-1 shrink-0 ml-2">
              <span
                className={`font-bold text-sm tabular-nums ${r.balance < 0 ? 'text-rose-500' : 'text-emerald-600'}`}
                data-testid={`trip-party-balance-${r.uid}`}
              >
                {r.balance >= 0 ? '+' : ''}{formatNXC(r.balance, currency)}
              </span>
              {ctx?.openMemberLedger && <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
            </div>
          </button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mt-2 italic">
        Tap a member to see their ledger.
      </p>
    </Card>
  );
}
